/** @jsx jsx */
import React, { useRef, useState, useEffect } from 'react'
import { jsx } from "theme-ui"
import styled from "styled-components"
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis, ResponsiveContainer, Pie } from "recharts"
import theme, { rem } from '@src/theme'
import { useOnScreen } from '@src/helpers/hooks/useOnScreen'

const COLORS = [
  theme.colors.purple,
  theme.colors.midBlue,
  theme.colors.turquoise,
  theme.colors.darkYellow,
  theme.colors.rainbowOrange,
  theme.colors.red,
  theme.colors.brown,
  theme.colors.transPink,
]

const formatPercent = (value) => {
  return `${Math.round(value * 100)}%`
}

const renderLabelList = ({ x, y, width, height, value }) => {
  return (
    <g>
      <text
        style={{
          fill: theme.colors.darkPurple,
          fontSize: rem(16),
          fontWeight: 500,
          textAnchor: "start",
          transform: 'translateY(-1rem)',
          animation: 'fadeIn 300ms ease',
        }}
        x={0}
        y={y + 4}
        fill="darkPurple"
        textAnchor="left"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  )
}

function HorizontalBarChart({ data }) {
  const dataByGroup = data.reduce((groups, item) => {
    const group = item.group || 'default'
    if (!groups[group]) {
      groups[group] = []
    }
    groups[group].push(item)
    return groups
  }, {})

  const groups = Object.keys(dataByGroup)

  const ref = useRef(null)
  const isOnScreen = useOnScreen(ref, true)
  const [selectedGroup, setSelectedGroup] = useState(groups[0])

  const currentData = selectedGroup ? dataByGroup[selectedGroup] : data

  const barSize = 44
  const barGap = 36
  const margin = { top: 8, bottom: -16 }
  const calculatedHeight = currentData.length * (barSize + barGap) + margin.top + margin.bottom

  return (
    <div ref={ref}>
      {groups.length > 1 && (
        <div sx={{
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: [1, null, null, 2]
        }}>
          {groups.map(group => (
            <button
              key={group}
              onClick={() => setSelectedGroup(group)}
              sx={{
                cursor: 'pointer',
                bg: selectedGroup === group ? 'darkPurple' : 'transparent',
                border: '2px solid',
                borderColor: 'darkPurple',
                color: selectedGroup === group ? 'white' : 'darkPurple',
                px: ['0.75rem', null, null, '1rem'],
                py: ['0.5rem', null, null, '.75rem'],
                borderRadius: 'calc(infinity * 1px)',
                fontSize: rem(16),
                transition: 'all 300ms ease',
                '&:hover': {
                  bg: selectedGroup === group ? 'darkPurple' : 'rgba(58, 42, 85, 0.1)',
                }
              }}
            >
              {group}
            </button>
          ))}
        </div>
      )}

      {isOnScreen ? (
        <ResponsiveContainer
          width="100%"
          height={calculatedHeight}
        >
          <BarChart
            data={currentData}
            layout="vertical"
            barSize={barSize}
            barGap={barGap}
            margin={margin}
            style={{
              transition: 'opacity 400ms ease-in-out', opacity: isOnScreen ? 1 : 0
            }}
          >
            <XAxis
              type="number"
              domain={[0, 1]}
              hide
            />
            <YAxis
              dataKey="name"
              type="category"
              axisLine={false}
              tickLine={false}
              hide
            />
            <Bar
              dataKey="value"
              radius={[4, 0, 0, 4]}
              background={{ fill: "#f5f5f5", radius: [4, 4, 4, 4] }}
              animationDuration={800}
            >
              {currentData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
              <LabelList
                dataKey="name"
                style={{
                  fill: theme.colors.darkPurple,
                  fontSize: rem(16),
                  fontWeight: 500,
                  textAnchor: "start",
                  transform: 'translateY(-1rem)',
                  animation: 'fadeIn 300ms ease',
                }}
                content={renderLabelList}
              />
              <LabelList
                dataKey="value"
                position="right"
                formatter={formatPercent}
                style={{
                  fill: theme.colors.darkPurple,
                  fontSize: rem(16),
                  fontWeight: 500,
                  animation: 'fadeIn 300ms ease'
                }}
                offset={20}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : <div sx={{ height: calculatedHeight, width: '100%' }} />}
    </div>
  )
}

export { HorizontalBarChart }
